import { IRangeFilters, IFilterType, IComplainerFilterItem } from 'src/@complaints/interfaces';
import {
  AircraftCategory,
  ComplaintSummaryFilter,
  ContactMethod,
  OperationType,
  OperatorCategory,
} from 'src/app/gql/__generated__/hooks';
import { convertSecondsToTimeString, convertTimeStringToSeconds } from 'src/utils';
import { FilterKeyConfig } from 'src/utils/filters/parseUrlFilters';

export const filterKeyMap: FilterKeyConfig[] = [
  // Strings
  { key: 'disturbanceTypes', type: 'string', displayName: 'reason', isArray: true },
  { key: 'statuses', type: 'string', isArray: true },
  { key: 'contactMethods', type: 'string', isArray: true },
  { key: 'operationTypes', type: 'string', isArray: true },
  { key: 'aircraftTypes', type: 'string', isArray: true },
  { key: 'aircraftCategories', type: 'string', isArray: true },
  { key: 'airlines', type: 'string', isArray: true },
  { key: 'operatorCategories', type: 'string', isArray: true },
  // Numbers
  { key: 'limitToIds', type: 'number', displayName: 'inquiryId', isArray: true },
  { key: 'complainerIds', type: 'number', displayName: 'complainerId', isArray: false },
  { key: 'operationIds', type: 'number', isArray: true },
  // Boolean
  { key: 'correlated', type: 'boolean', isArray: false }, // hasOperation
  { key: 'responseRequired', type: 'boolean', isArray: false },
  // Dates
  { key: 'timePeriod', type: 'timeRange', isArray: false },
];

export const urlToComplaintFilterState = (
  input: ComplaintSummaryFilter
): {
  time: IRangeFilters;
  filters: IFilterType;
  complaintID: string;
  complainer: IComplainerFilterItem;
} => {
  const tableFilters: IFilterType = {
    disturbanceTypes: [],
    hasOperation: [],
    statuses: [],
    contactMethods: [],
    responseRequired: [],
  };
  const time = { from: '', to: '' };
  let complaintID = '';
  const complainer: IComplainerFilterItem = { id: null, name: null };

  Object.entries(input).forEach(([key, value]) => {
    if (key === 'limitToIds') {
      // Store in complaintId
      complaintID = value.toString();
    } else if (key === 'correlated') {
      if (!value) {
        tableFilters.hasOperation.push({
          key: 'No Flight',
          label: 'No Flight',
          icon: '',
          className: '',
        });
      } else if (value) {
        tableFilters.hasOperation.push({
          key: 'Flight',
          label: 'Flight',
          icon: '',
          className: '',
        });
      }
    } else if (key === 'responseRequired') {
      if (!value) {
        tableFilters.responseRequired.push({
          key: 'No',
          label: 'No',
          icon: '',
          className: '',
        });
      } else if (value) {
        tableFilters.responseRequired.push({
          key: 'Yes',
          label: 'Yes',
          icon: '',
          className: '',
        });
      }
    } else if (key === 'complainerIds') {
      // Set complainer name to null to trigger fetch later
      complainer.id = Number(value);
      complainer.name = null;
    } else if (key === 'contactMethods' && Array.isArray(value)) {
      tableFilters.contactMethods.push(...(value as ContactMethod[]));
    } else if (key === 'timePeriod') {
      const arr = value as Record<string, number>;
      if (arr && typeof arr.start === 'number' && typeof arr.end === 'number') {
        time.from = convertSecondsToTimeString(arr.start);
        time.to = convertSecondsToTimeString(arr.end);
      }
    } else {
      const items = value as string[] | number[];
      if (items && items.length > 0) {
        tableFilters[key] = items.map((category: string | number) => ({
          key: category,
          label: category,
          icon: undefined,
          className: '',
        }));
      }
    }
  });

  return {
    filters: tableFilters,
    time,
    complaintID,
    complainer,
  };
};

export const tableFiltersToComplaintSummaryFilter = (
  tableFilters: IFilterType,
  time: IRangeFilters,
  complaintID: string,
  complainer: IComplainerFilterItem
): {
  filter: Partial<ComplaintSummaryFilter>;
} => {
  const result: {
    disturbanceTypes?: string[];
    limitToIds?: number[];
    timePeriod?: { start: number; end: number };
    correlated?: boolean;
    statuses?: string[];
    complainerIds?: number[];
    contactMethods?: ContactMethod[];
    operationIds?: number[];
    operationTypes?: OperationType[];
    aircraftTypes?: string[];
    aircraftCategories?: AircraftCategory[];
    airlines?: string[];
    operatorCategories?: OperatorCategory[];
    responseRequired?: boolean;
  } = {};
  if (time.from && time.to) {
    result.timePeriod = {
      start: convertTimeStringToSeconds(time.from),
      end: convertTimeStringToSeconds(time.to),
    };
  }
  if (complaintID) {
    result.limitToIds = [Number(complaintID)];
  }

  if (tableFilters.responseRequired.length > 0) {
    result.responseRequired = tableFilters.responseRequired.some(item => item.key === 'Yes') ? true : false;
  }
  if (tableFilters.disturbanceTypes.length > 0) {
    result.disturbanceTypes = tableFilters.disturbanceTypes.map(item => item.key);
  }
  if (tableFilters.hasOperation.length > 0) {
    result.correlated = tableFilters.hasOperation.some(item => item.key === 'Flight');
  }
  if (tableFilters.statuses.length > 0) {
    result.statuses = tableFilters.statuses.map(item => item.key);
  }
  if (complainer && complainer.id > 0) {
    result.complainerIds = [complainer.id];
  }
  if (tableFilters.contactMethods && tableFilters.contactMethods.length > 0) {
    result.contactMethods = tableFilters.contactMethods;
  }

  if (tableFilters.operationIds && tableFilters.operationIds.length > 0) {
    result.operationIds = tableFilters.operationIds.map(item => Number(item.key));
  }
  if (tableFilters.operationTypes && tableFilters.operationTypes.length > 0) {
    result.operationTypes = tableFilters.operationTypes.map(item => item.key as OperationType);
  }
  if (tableFilters.aircraftTypes && tableFilters.aircraftTypes.length > 0) {
    result.aircraftTypes = tableFilters.aircraftTypes.map(item => item.key);
  }
  if (tableFilters.aircraftCategories && tableFilters.aircraftCategories.length > 0) {
    result.aircraftCategories = tableFilters.aircraftCategories.map(
      item => item.key as AircraftCategory
    );
  }
  if (tableFilters.airlines && tableFilters.airlines.length > 0) {
    result.airlines = tableFilters.airlines.map(item => item.key);
  }
  if (tableFilters.operatorCategories && tableFilters.operatorCategories.length > 0) {
    result.operatorCategories = tableFilters.operatorCategories.map(
      item => item.key as OperatorCategory
    );
  }

  return {
    filter: result,
  };
};
